import type { MeResponse } from "@safeplate/contracts";
import { Badge, Button, cn } from "@safeplate/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import {
  BellRing,
  Building2,
  CalendarCheck2,
  ClipboardCheck,
  ClipboardPlus,
  CreditCard,
  FileClock,
  FileText,
  LayoutDashboard,
  ListChecks,
  LockKeyhole,
  LogOut,
  MapPin,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { signOut } from "../api/auth.js";
import { ApiError } from "../api/errors.js";
import { getLocations, setActiveOrganisationHeader } from "../api/client.js";
import { Brand } from "../components/brand.js";
import { SelectControl } from "../components/select-control.js";
import {
  organisationChoices,
  readStoredSelection,
  resolveSelection,
  selectionKeys,
  storeSelection,
} from "../state/selection.js";
import { ShellContext } from "./shell-context.js";
import {
  lockSharedDevice,
  removeCurrentDeviceAccount,
} from "../quick-access/quick-access-api.js";

const navigation: readonly {
  items: readonly {
    adminOnly?: boolean;
    icon: LucideIcon;
    label: string;
    to: string;
  }[];
  label: string;
}[] = [
  {
    label: "Work",
    items: [
      { icon: LayoutDashboard, label: "Today", to: "/" },
      { icon: ClipboardPlus, label: "New log", to: "/log" },
      { icon: FileClock, label: "Records", to: "/records" },
      { icon: BellRing, label: "Attention", to: "/attention" },
    ],
  },
  {
    label: "Manage",
    items: [
      { icon: ListChecks, label: "Tasks", to: "/tasks" },
      { icon: Users, label: "Team", to: "/team" },
      { icon: Settings2, label: "Setup", to: "/setup" },
      { icon: FileText, label: "Reports", to: "/reports" },
      {
        adminOnly: true,
        icon: ShieldCheck,
        label: "Privacy",
        to: "/privacy",
      },
      {
        adminOnly: true,
        icon: CreditCard,
        label: "Billing",
        to: "/billing",
      },
      {
        icon: SlidersHorizontal,
        label: "Notifications",
        to: "/settings",
      },
    ],
  },
] as const;

function NavLink({
  icon: Icon,
  label,
  to,
}: {
  icon: LucideIcon;
  label: string;
  to: string;
}) {
  return (
    <Link
      activeOptions={{ exact: to === "/" }}
      activeProps={{ className: "bg-emerald-800 text-white shadow-sm" }}
      className="group flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-semibold text-stone-600 transition hover:bg-stone-100 hover:text-stone-950"
      to={to}
    >
      <Icon
        className="size-[1.05rem] shrink-0"
        strokeWidth={2.1}
        aria-hidden="true"
      />
      {label}
    </Link>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function AppShell({ me }: { me: MeResponse }) {
  const organisations = useMemo(() => organisationChoices(me), [me]);
  const [activeOrganisationId, setActiveOrganisationId] = useState(
    () =>
      resolveSelection(
        organisations,
        readStoredSelection(selectionKeys.organisation),
      ) ?? organisations[0]!.id,
  );
  const [activeLocationId, setActiveLocationId] = useState<string | null>(() =>
    readStoredSelection(selectionKeys.location),
  );
  const [signingOut, setSigningOut] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    setActiveOrganisationHeader(activeOrganisationId);
    storeSelection(selectionKeys.organisation, activeOrganisationId);
  }, [activeOrganisationId]);

  const locationsQuery = useQuery({
    enabled: Boolean(activeOrganisationId),
    queryFn: () => getLocations(activeOrganisationId),
    queryKey: ["locations", activeOrganisationId],
  });

  const locations = locationsQuery.data ?? [];
  const resolvedLocationId = resolveSelection(locations, activeLocationId);
  const activeLocation =
    locations.find((location) => location.id === resolvedLocationId) ?? null;

  useEffect(() => {
    if (resolvedLocationId !== activeLocationId) {
      setActiveLocationId(resolvedLocationId);
    }
    storeSelection(selectionKeys.location, resolvedLocationId);
  }, [activeLocationId, resolvedLocationId]);

  const activeOrganisation =
    organisations.find(
      (organisation) => organisation.id === activeOrganisationId,
    ) ?? organisations[0]!;

  function changeOrganisation(orgId: string) {
    setActiveOrganisationHeader(orgId);
    setActiveOrganisationId(orgId);
    setActiveLocationId(null);
    storeSelection(selectionKeys.location, null);
  }

  async function logOut() {
    setSigningOut(true);
    try {
      await removeCurrentDeviceAccount();
      try {
        await signOut();
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 401)) throw error;
      }
      setActiveOrganisationHeader(null);
      queryClient.clear();
      await navigate({ to: "/lock" });
    } finally {
      setSigningOut(false);
    }
  }

  async function lock() {
    setSigningOut(true);
    try {
      await lockSharedDevice();
      setActiveOrganisationHeader(null);
      queryClient.clear();
      await navigate({ to: "/lock" });
    } finally {
      setSigningOut(false);
    }
  }

  const shellContext = {
    activeLocation,
    activeOrganisationId,
    activeRole: activeOrganisation.role,
    locationError: locationsQuery.error,
    locations,
    locationsPending: locationsQuery.isPending,
    ownStaffProfileIds: me.staff_profiles
      .filter((profile) => profile.org_id === activeOrganisationId)
      .map((profile) => profile.staff_profile_id),
    refreshLocations: locationsQuery.refetch,
  };

  return (
    <ShellContext.Provider value={shellContext}>
      <div className="min-h-dvh bg-canvas text-stone-950">
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-stone-200 bg-surface px-4 py-5 md:flex md:flex-col">
          <div className="px-2">
            <Brand />
          </div>
          <nav
            className="mt-9 flex-1 space-y-7"
            aria-label="Primary navigation"
          >
            {navigation.map((group) => (
              <div key={group.label}>
                <p className="mb-2 px-3 text-[0.65rem] font-bold tracking-[0.14em] text-stone-400 uppercase">
                  {group.label}
                </p>
                <div className="space-y-1">
                  {group.items
                    .filter(
                      (item) =>
                        !item.adminOnly || activeOrganisation.role === "admin",
                    )
                    .map((item) => (
                      <NavLink key={item.to} {...item} />
                    ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="border-t border-stone-200 pt-4">
            <div className="flex items-center gap-3 px-2">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-emerald-100 text-xs font-black text-emerald-900">
                {initials(me.user.name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{me.user.name}</p>
                <p className="truncate text-xs capitalize text-stone-500">
                  {activeOrganisation.role}
                </p>
              </div>
              <div className="flex shrink-0">
                <Button
                  aria-label="Lock SafePlate"
                  disabled={signingOut}
                  onClick={() => void lock()}
                  size="icon"
                  variant="ghost"
                >
                  <LockKeyhole className="size-4" aria-hidden="true" />
                </Button>
                <Button
                  aria-label="Sign out"
                  disabled={signingOut}
                  onClick={() => void logOut()}
                  size="icon"
                  variant="ghost"
                >
                  <LogOut className="size-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>
        </aside>

        <div className="md:pl-64">
          <header className="sticky top-0 z-20 border-b border-stone-200/90 bg-canvas/92 px-4 py-3 backdrop-blur-xl sm:px-6 lg:px-9">
            <div className="mx-auto flex max-w-[1440px] items-center gap-3">
              <div className="mr-auto md:hidden">
                <Brand compact />
              </div>
              <div className="hidden min-w-0 flex-1 md:block">
                <p className="truncate text-xs font-bold text-stone-500">
                  {activeOrganisation.name}
                </p>
                <p className="truncate text-sm font-black">
                  {activeLocation?.name ?? "Choose a location"}
                </p>
              </div>
              {organisations.length > 1 ? (
                <SelectControl
                  ariaLabel="Organisation"
                  icon={
                    <Building2
                      className="size-4 shrink-0 text-stone-400"
                      aria-hidden="true"
                    />
                  }
                  onChange={changeOrganisation}
                  options={organisations.map((organisation) => ({
                    label: organisation.name,
                    value: organisation.id,
                  }))}
                  value={activeOrganisationId}
                />
              ) : null}
              <SelectControl
                ariaLabel="Location"
                disabled={locationsQuery.isPending || locations.length === 0}
                icon={
                  <MapPin
                    className="size-4 shrink-0 text-stone-400"
                    aria-hidden="true"
                  />
                }
                onChange={(locationId) =>
                  setActiveLocationId(locationId || null)
                }
                options={
                  locations.length > 0
                    ? locations.map((location) => ({
                        label: location.name,
                        value: location.id,
                      }))
                    : [
                        {
                          label: locationsQuery.isPending
                            ? "Loading locations…"
                            : "No locations",
                          value: "",
                        },
                      ]
                }
                value={resolvedLocationId ?? ""}
              />
              <Button
                aria-label="Lock SafePlate"
                className="md:hidden"
                disabled={signingOut}
                onClick={() => void lock()}
                size="icon"
                variant="ghost"
              >
                <LockKeyhole className="size-4" aria-hidden="true" />
              </Button>
              {activeLocation ? (
                <Badge
                  className="hidden capitalize sm:inline-flex"
                  tone={
                    activeLocation.status === "active" ? "positive" : "neutral"
                  }
                >
                  {activeLocation.status}
                </Badge>
              ) : null}
            </div>
          </header>

          <main className="mx-auto max-w-[1440px] px-4 py-6 pb-24 sm:px-6 lg:px-9 lg:py-8 md:pb-8">
            {activeLocation &&
            !["active", "trialing"].includes(
              activeLocation.entitlement_state,
            ) ? (
              <div
                className={cn(
                  "mb-5 rounded-xl border px-4 py-3 text-sm",
                  ["read_only", "cancelled"].includes(
                    activeLocation.entitlement_state,
                  )
                    ? "border-red-200 bg-red-50 text-red-950"
                    : "border-amber-200 bg-amber-50 text-amber-950",
                )}
              >
                <p className="font-black">
                  {activeLocation.entitlement_state === "none"
                    ? "This location needs a licence"
                    : activeLocation.entitlement_state === "past_due"
                      ? "Payment recovery is underway; logging continues"
                      : activeLocation.entitlement_state === "grace"
                        ? "This location is in billing grace; logging continues"
                        : "This location is read-only; evidence and reports remain available"}
                </p>
                <p className="mt-1 text-xs opacity-75">
                  {activeOrganisation.role === "admin" ? (
                    <Link
                      className="font-bold underline underline-offset-2"
                      to="/billing"
                    >
                      Open billing and licence settings
                    </Link>
                  ) : (
                    "Ask an organisation Admin to review billing and licence capacity."
                  )}
                </p>
              </div>
            ) : null}
            <Outlet />
          </main>
        </div>

        <nav
          aria-label="Mobile navigation"
          className="fixed inset-x-3 bottom-3 z-40 grid grid-cols-4 rounded-2xl border border-stone-200 bg-surface/95 p-1.5 shadow-lg backdrop-blur-xl md:hidden"
        >
          {[
            { icon: LayoutDashboard, label: "Today", to: "/" },
            { icon: CalendarCheck2, label: "Tasks", to: "/tasks" },
            { icon: ClipboardCheck, label: "Records", to: "/records" },
            { icon: ClipboardPlus, label: "Log", to: "/log" },
          ].map((item) => (
            <Link
              activeOptions={{ exact: item.to === "/" }}
              activeProps={{ className: "bg-emerald-800 text-white" }}
              className={cn(
                "flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[0.65rem] font-bold text-stone-500",
              )}
              key={item.to}
              to={item.to}
            >
              <item.icon className="size-4" aria-hidden="true" />
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </ShellContext.Provider>
  );
}
